import { User } from 'types'
import {
  ArrowTopRightOnSquareIcon,
  Avatar,
  AvatarProps,
  Box,
  BriefcaseIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  Flex,
  IconButton,
  Text,
  Tooltip,
  TooltipProps,
} from './index'

export interface Props extends AvatarProps {
  hideTooltip?: boolean
  user: User | undefined
  tooltipProps?: Omit<TooltipProps, 'content'>
}

export default function UserAvatar({ hideTooltip = false, user, tooltipProps, ...rest }: Props) {
  if (!user) return null

  const avatar = <Avatar name={user.displayName} src={user.photoUrl} size="sm" {...rest} />
  if (hideTooltip) return avatar

  return (
    <Tooltip
      interactive
      positioning={{ placement: 'bottom' }}
      content={<TooltipContent {...user} />}
      {...tooltipProps}
    >
      <Box as="span" display="inline-flex">
        {avatar}
      </Box>
    </Tooltip>
  )
}

function TooltipContent(props: User) {
  const { displayName, photoUrl, company, title, email } = props

  return (
    <Flex direction="column" gap={2} py={1}>
      <Flex alignItems="center" gap={3}>
        <Avatar name={displayName} src={photoUrl} size="md" />
        <Text textStyle="bodyMedium" fontWeight="semibold">
          {displayName}
        </Text>
      </Flex>
      {company && (
        <Flex alignItems="center" gap={2}>
          <BuildingOfficeIcon fontSize="sm" type="outline" />
          <Text textStyle="bodySmall">{company}</Text>
        </Flex>
      )}
      {title && (
        <Flex alignItems="center" gap={2}>
          <BriefcaseIcon fontSize="sm" type="outline" />
          <Text textStyle="bodySmall">{title}</Text>
        </Flex>
      )}
      <Flex alignItems="center" gap={2}>
        <EnvelopeIcon fontSize="sm" type="outline" />
        <Text textStyle="bodySmall">{email}</Text>
        <IconButton
          aria-label="Open email client"
          icon={<ArrowTopRightOnSquareIcon fontSize="sm" type="outline" />}
          size="xs"
          variant="ghost"
          onClick={() => window.open(`mailto:${email}`, '_self')}
        />
      </Flex>
    </Flex>
  )
}
